import React from "react";

import AddIcon from "@mui/icons-material/Add";
import CloudSyncOutlinedIcon from "@mui/icons-material/CloudSyncOutlined";
// import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { Box, Button } from "@mui/material";
import { EditSquare } from "assets/material-custom-icons";
import { InlineFilter } from "components/Filters/InlineFilter";
import { RoleComponentGuard } from "components/guards/Role-component.guard";
import { DealCreate, DealCreateFromFile } from "components/modals";
import { Menu } from "components/popups";
import { DealViews } from "constants/deal";
// import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useCanCreateDeal, useNavigate, usePrompt } from "hooks";
import {
  useAccountSelector,
  useDealActions,
  useDealFiltersSelector,
  useDealQueriesSelect,
  useReportsActions
} from "store";

import { RoleName } from "../../../constants";
import { StyledStack } from "../Dashboard/StyledStack";
import { InputFilter } from "../Filters/InputFilter";
import { OrderBy } from "../OrderBy/OrderBy";
import { PipelineControls } from "../Pipeline/PipelineControls";
import { PipelineViewToggle } from "../Pipeline/PipelineViewToggle";

export const DealsToolbar: React.FC = () => {
  const { updateQueries } = useDealActions();
  const { setQueriesAction } = useReportsActions();
  const { filters: queriesFilters, view } = useDealQueriesSelect();
  const filtersList = useDealFiltersSelector();
  const openPrompt = usePrompt();
  const navigate = useNavigate();
  const canCreateDeal = useCanCreateDeal();
  const {
    show_global_investor_filter,
    allow_dataroom: isDataRoomsEnabled,
    allow_import_deal_from_excel: isImportFromExcelEnabled,
    allow_deals_grouping: isDealsGroupingEnabled
  } = useAccountSelector() || {};

  const showViewToggle = view === DealViews.PIPELINE && !!isDealsGroupingEnabled;
  const showPipelineControls =
    (view === DealViews.PIPELINE || view === DealViews.TABLE) && !!isDealsGroupingEnabled;

  // The grouping controls (View toggle, Group by, collapse/expand, settings) make a single row too crowded,
  // so the toolbar switches to the two-row layout (Figma 2701:66221) whenever they're shown; otherwise it
  // stays on one row (Figma 2693:64275).
  const compact = showViewToggle || showPipelineControls;

  const createDeal = async () => {
    const data = await openPrompt({ Component: DealCreate, props: {} });

    if (!data) return;

    navigate({ route: "deal", params: { deal_id: data.id } });
  };

  const createDealFromFile = async () => {
    const data = await openPrompt({ Component: DealCreateFromFile });

    if (!data) return;

    navigate({ route: "deal", params: { deal_id: data.id } });
  };

  const filtersBlock = (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        flex: "1 1 auto",
        minWidth: 0,
        flexWrap: "wrap",
        maxHeight: "125px",
        overflow: "auto"
      }}
    >
      <InlineFilter
        handleChangeFilter={(filters) => {
          updateQueries({ filters });
          show_global_investor_filter && setQueriesAction({ filters });
        }}
        filtersList={filtersList}
        applyMode={true}
        state={queriesFilters}
      />
    </Box>
  );

  const searchBlock = <InputFilter />;
  const orderByBlock = <OrderBy />;
  const viewToggleBlock = showViewToggle ? <PipelineViewToggle /> : null;
  const pipelineControlsBlock = showPipelineControls ? <PipelineControls /> : null;

  const addDealBlock = (
    <RoleComponentGuard availableRoles={[RoleName.Admin, RoleName.Analyst]} hideChild={!canCreateDeal}>
      {isDataRoomsEnabled ? (
        <Menu
          iconButtonProps={{ sx: { padding: 0, width: "auto" } }}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          icon={
            <Button
              sx={{
                textTransform: "none",
                maxHeight: "27px !important",
                minHeight: "27px !important",
                whiteSpace: "nowrap"
              }}
              startIcon={<AddIcon />}
              variant="contained"
            >
              Add Deal
            </Button>
          }
          options={[
            {
              title: "Import From Dataroom / Investor Portal",
              icon: <CloudSyncOutlinedIcon sx={{ color: "#0a3142" }} />,
              onClick: () => {
                navigate({ route: "dataRooms" });
              }
            },
            {
              title: "Start From Scratch",
              icon: <EditSquare id="createDealModal" sx={{ color: "#0a3142", fill: "#0a3142" }} />,
              onClick: createDeal
            },
            {
              title: "Starting From File",
              icon: <UploadFileIcon sx={{ color: "#0a3142" }} />,
              onClick: createDealFromFile,
              hidden: !isImportFromExcelEnabled
            }
          ]}
        />
      ) : (
        <Button
          sx={{
            textTransform: "none",
            maxHeight: "27px !important",
            minHeight: "27px !important",
            whiteSpace: "nowrap"
          }}
          variant="contained"
          startIcon={<AddIcon />}
          onClick={createDeal}
        >
          Add Deal
        </Button>
      )}
    </RoleComponentGuard>
  );

  return (
    <StyledStack sx={{ flexDirection: "column", alignItems: "stretch" }}>
      {compact ? (
        // Two rows: filters + search/actions on top, pipeline view controls below (View left, controls right).
        <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", width: "100%" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px", width: "100%" }}>
            {filtersBlock}
            {searchBlock}
            {orderByBlock}
            {addDealBlock}
          </Box>
          {(viewToggleBlock || pipelineControlsBlock) && (
            <Box sx={{ display: "flex", alignItems: "center", gap: "10px", width: "100%" }}>
              {viewToggleBlock}
              <Box sx={{ flex: "1 1 auto" }} />
              {pipelineControlsBlock}
            </Box>
          )}
        </Box>
      ) : (
        // Single row: filters grow, then pipeline controls, search, view toggle, and the create action.
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px", width: "100%", flexWrap: "wrap" }}>
          {filtersBlock}
          {pipelineControlsBlock}
          {searchBlock}
          {viewToggleBlock}
          {orderByBlock}
          {addDealBlock}
        </Box>
      )}
    </StyledStack>
  );
};
